import { Button, TextField, Chip } from "@mui/material"
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "elements/Box";
import { ProjectConfig } from 'Global';
import { useSnackbar, withSnackbar } from "notistack";
import LangTabs from "elements/LangTabs";
import App from "elements/HtmlEditor/App";

export default withSnackbar(
  function OracionesLiturgicasEditor(props: any) {

    /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
    const { enqueueSnackbar } = useSnackbar();
    /*============================================= */

    /*=======================================
    GET ID FROM URL
    =======================================*/
    let { oracion_id } = useParams();
    /*=======================================*/

    const navigate = useNavigate();
    
    const defaultValues: any = {
      id: oracion_id ? oracion_id : "null",
      titulo: "",
      contentHTML: "",
      contentJSON: "",
      date_type: "",
    };
    
    const [loading, setLoading] = useState(false); 
    const [formValues, setFormValues] = useState(defaultValues);

    // i18n: idioma actual y traducciones
    const [currentLangId, setCurrentLangId] = useState<string>("1");
    const [translations, setTranslations] = useState<Record<string, any>>({});
    
    // Estado para el contenido del editor WYSIWYG
    const [initialContent, setInitialContent] = useState<any>(null);

    // Helper para parsear JSON de forma segura
    const safeParseJSON = (jsonData: any) => {
      if (!jsonData) return null;
      if (typeof jsonData === 'object') return jsonData;
      if (typeof jsonData === 'string') {
        try {
          return JSON.parse(jsonData);
        } catch (e) {
          return null;
        }
      }
      return null;
    };

    // Handler para cambio de idioma
    const handleLangChange = (newLangId: string) => {
      setCurrentLangId(newLangId);
      // Actualizar el editor con el contenido del nuevo idioma
      const trans = translations[newLangId];
      if (trans) {
        setInitialContent(safeParseJSON(trans.contentJSON));
      } else {
        setInitialContent(null);
      }
    };

    // Helper para obtener el título según idioma
    const getCurrentTitulo = () => {
      if (currentLangId === "1") return formValues.titulo || "";
      return translations[currentLangId]?.titulo || "";
    };

    // Handler para cambio de título
    const handleTituloChange = (e: { target: { value: any } }) => {
      const value = e.target.value;
      if (currentLangId === "1") {
        setFormValues({ ...formValues, titulo: value });
      } else {
        setTranslations(prev => ({
          ...prev,
          [currentLangId]: {
            ...prev[currentLangId],
            titulo: value,
            contentHTML: prev[currentLangId]?.contentHTML || "",
            contentJSON: prev[currentLangId]?.contentJSON || null
          }
        }));
      }
    };

    // Handler para cambios del editor WYSIWYG
    const handleEditorChange = (htmlContent: any, jsonContent: any) => {
      if (currentLangId === "1") {
        setFormValues({
          ...formValues,
          contentHTML: htmlContent,
          contentJSON: jsonContent,
        });
      } else {
        setTranslations(prev => ({
          ...prev,
          [currentLangId]: {
            ...prev[currentLangId],
            titulo: prev[currentLangId]?.titulo || "",
            contentHTML: htmlContent,
            contentJSON: jsonContent
          }
        }));
      }
    };

    /*=======================================
    GET DETAILS IF EDITING
    =======================================*/
    useEffect(() => {
      if (oracion_id) {
        axios
          .get(ProjectConfig.api_url, {
            params: {
              request: "oraciones_liturgicas_admin",
              oracion_id: oracion_id,
            },
          })
          .then((response) => {
            console.log("oracion_data", response.data);
            if (response.data.success && response.data.oracion) {
              const oracion = response.data.oracion;
              setFormValues({
                id: oracion.id,
                titulo: oracion.titulo,
                contentHTML: oracion.contentHTML || "",
                contentJSON: oracion.contentJSON || "",
                date_type: oracion.date_type,
              });
              // Establecer contenido inicial del editor
              setInitialContent(safeParseJSON(oracion.contentJSON));
              
              // Cargar traducciones parseando los JSON de forma segura
              if (oracion.translations) {
                const parsedTranslations: Record<string, any> = {};
                Object.keys(oracion.translations).forEach(langId => {
                  const trans = oracion.translations[langId];
                  parsedTranslations[langId] = {
                    titulo: trans.titulo,
                    contentHTML: trans.contentHTML,
                    contentJSON: safeParseJSON(trans.contentJSON)
                  };
                });
                setTranslations(parsedTranslations);
              }
            }
            setLoading(true);
          })
          .catch((err) => {
            console.log(err.data);
            setLoading(true);
          });
      }
    }, [oracion_id]);
    /*=======================================*/

    /*=========================================================
    GET DATE TYPE DISPLAY NAME         
    =========================================================*/
    const getDateTypeDisplayName = (dateType: string) => {
      const typeMap: { [key: string]: string } = {
        'oficio_lectura': 'Oficio de Lectura',
        'laudes': 'Laudes',
        'intermedias': 'Horas Intermedias',
        'visperas': 'Vísperas',
        'completas': 'Completas',
        'oraciones': 'Oraciones'
      };
      return typeMap[dateType] || dateType;
    };

    /*=======================================
    SUBMIT
    =======================================*/
    const handleSubmit = () => {
      // Preparar formValues con traducciones
      const formValuesWithTranslations = {
        ...formValues,
        translations: translations
      };

      console.log("Saving:", formValuesWithTranslations);

      const bodyFormData = new FormData();
      bodyFormData.append("request", "save_oracion_liturgica");
      bodyFormData.append("formValues", JSON.stringify(formValuesWithTranslations));

      axios
        .post(ProjectConfig.api_url, bodyFormData)
        .then((response) => {
          console.log(response);
          if (response.data.success) {
            enqueueSnackbar("Guardado exitosamente", { variant: "success" });
            navigate("/admin/oraciones-liturgicas");
          } else {
            enqueueSnackbar(response.data.error || "Error al guardar", { variant: "error" });
          }
        })
        .catch((err) => {
          console.log(err.data);
          enqueueSnackbar("Error de conexión", { variant: "error" });
        });
    };
    /*====================================*/

    return (
      <div id="OracionesLiturgicasEditor">
        <div className="userForm_right">
          <Box id="oracion_liturgica_editor" className="box_editor" title="Oración Litúrgica" subtitle="Editar">
            
            {/* Mostrar tipo de oración */}
            {formValues.date_type && (
              <div style={{ marginBottom: 16 }}>
                <Chip 
                  label={getDateTypeDisplayName(formValues.date_type)} 
                  variant="outlined" 
                  color="primary"
                />
              </div>
            )}

            {/* Tabs de idioma */}
            {oracion_id && oracion_id !== "null" && (
              <LangTabs 
                currentLangId={currentLangId} 
                onChange={handleLangChange} 
              />
            )}

            <div className='input_group'>
              <div className='input_row'>
                <TextField 
                  id="titulo"
                  name="titulo"
                  label="Título"
                  type="text"
                  required={currentLangId === "1"}
                  fullWidth
                  multiline
                  rows={2}
                  value={getCurrentTitulo()}
                  onChange={handleTituloChange}
                />
              </div>

              <div className='input_row'>
                <label>Contenido</label>
                <App
                  key={`content-${currentLangId}`}
                  placeholder="Contenido de la oración litúrgica"
                  handleEditorChange={handleEditorChange}
                  content={initialContent}
                />
              </div>
            </div>

            <div className='buttons'>
              <Button onClick={() => navigate(-1)} variant="outlined" className="button_2">Cancelar</Button>
              <Button onClick={handleSubmit} variant="contained" className="button_1">Guardar</Button>
            </div>
          </Box>
        </div>
      </div>
    );
  }
);